import { useContext, useEffect, useState } from 'react';
import testService from 'ApiService/Teacher/TestService';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import DateField from 'components/fields/dateField';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import Nodatafound from 'pages/Common/Nodatafound';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const dayMap = {
  Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'Th',
  Friday: 'F', Saturday: 'S', Sunday: 'Su',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  return `${day}${month}${year}`;
};

const formatSchedule = (daySchedules) => {
  if (!daySchedules || daySchedules.length === 0) return '';
  return daySchedules
    .map((s) => {
      const d = dayMap[s.day] || s.day;
      const t = s.startTime.replace(':00', '').replace(' ', '');
      return `${d}-${t}`;
    })
    .join(' | ');
};

const getBranchInitials = (branches) => {
  if (!branches || branches.length === 0) return '';
  return branches
    .map((br) => (br.name || '').split(',').pop().trim()[0] || '')
    .join('');
};

const MultiSelect = ({ options, selected = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleSelect = (batchId) => {
    const updatedSelection = selected.includes(batchId)
      ? selected.filter((id) => id !== batchId)
      : [...selected, batchId];
    onChange(updatedSelection);
  };

  const handleOutsideClick = (e) => {
    if (!e.target.closest('.multiselect-container')) {
      setIsOpen(false);
    }
  };

  // close dropdown when clicked outside
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    } else {
      document.removeEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  const getBatchDisplay = (batch) => {
    if (!batch) return '';
    const num = `B${batch.batchNumber}`;
    const date = formatDate(batch.startDate);
    const sched = formatSchedule(batch.daySchedules);
    const branch = getBranchInitials(batch.branches);
    let display = num;
    if (date) display += ` | ${date}`;
    if (sched) display += ` (${sched})`;
    if (branch) display += ` ${branch}`;
    return display;
  };

  const getChipLabel = (batch) => {
    if (!batch) return '';
    const branch = getBranchInitials(batch.branches);
    return branch ? `B${batch.batchNumber} (${branch})` : `B${batch.batchNumber}`;
  };

  return (
    <div className="multiselect-container relative">
      <div
        onClick={handleToggle}
        className="flex min-h-12 cursor-pointer flex-wrap gap-2 rounded-lg border border-gray-800 bg-white p-3 shadow-sm transition-all hover:border-gray-300 dark:bg-gray-800"
      >
        {selected.map((batchId) => {
          const batch = options.find((b) => b._id === batchId);
          return (
            <span
              key={batchId}
              className="inline-flex items-center rounded-md bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {getChipLabel(batch)}
              <button
                className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(batchId);
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        {selected.length === 0 && (
          <span className="text-gray-500 dark:text-gray-400">
            Select batches
          </span>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {options.map((option) => (
            <div
              key={option._id}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option._id);
              }}
              className={`flex cursor-pointer items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                selected.includes(option._id)
                  ? 'bg-blue-50 dark:bg-blue-900/50'
                  : ''
              }`}
            >
              <input
                type="checkbox"
                readOnly
                checked={selected.includes(option._id)}
                className="mr-3 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm dark:text-white whitespace-nowrap">
                {getBatchDisplay(option)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SelectTest = ({
  testData,
  selectedTest,
  setSelectedBatches,
  setSelectedTest,
  selectedBatches,
  setscheduledAt,
  scheduledAt,
  startTime,
  setStartTime,
}) => {
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('mcq');

  useEffect(() => {
    if (state?.user && testData.courseId && testData.structureId) {
      fetchTests();
      fetchBatches();
    }
  }, [state?.user, testData.courseId, testData.structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTests = async () => {
    if (!testData.structureId) {
      setTests([]);
      return;
    }
    setLoading(true);
    try {
      const response = await testService.testByBatchStructure(testData.structureId);
      if (response.data.success) {
        setTests(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch tests.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch tests.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    if (!testData.courseId || !testData.structureId) {
      setBatches([]);
      return;
    }
    try {
      const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;
      const response = await batchTeacherStructureService.getBatchesByCourseStructure(
        testData.courseId,
        testData.structureId,
        selectedBranch
      );
      if (response.data.success) {
        setBatches(response.data.batches || []);
      } else {
        toast.error(response.data.message || 'Failed to fetch batches.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch batches.');
    }
  };

  if (!testData.courseId) {
    return <Nodatafound title="Please select a course first" />;
  }

  if (!testData.structureId) {
    return <Nodatafound title="Please select a batch structure first" />;
  }

  const filteredTests = tests.filter((t) => t.type === filterType);

  return (
    <div className="space-y-6 rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Select Batches to Assign
        </label>
        <MultiSelect
          options={batches}
          selected={selectedBatches}
          onChange={setSelectedBatches}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-1.5">
          <DateField
            label="Schedule At"
            id="scheduledAt"
            value={scheduledAt}
            onChange={(e) => setscheduledAt(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <InputField
            label="Start Time"
            type="time"
            id="startTime"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
      </div>

      <div className="flex space-x-2">
        <button
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filterType === 'mcq'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
          }`}
          onClick={() => setFilterType('mcq')}
        >
          MCQ Tests
        </button>
        <button
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            filterType === 'qa'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200'
          }`}
          onClick={() => setFilterType('qa')}
        >
          Q&A Tests
        </button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <BallTriangle color="#3b82f6" />
        </div>
      ) : filteredTests.length === 0 ? (
        <div className="flex h-40 items-center justify-center">
          <Nodatafound
            title={`No ${filterType.toUpperCase()} tests found for this batch structure`}
          />
        </div>
      ) : (
        <div className="grid max-h-[68vh] gap-4 overflow-hidden overflow-y-scroll rounded-xl border border-gray-200 p-2 dark:border-white/10  sm:grid-cols-2 lg:grid-cols-3 ">
          {filteredTests.map((test, i) => (
            <div
              key={test._id}
              onClick={() => setSelectedTest(test._id)}
              className={`cursor-pointer rounded-lg border p-4 transition-all hover:border-blue-500 ${
                selectedTest === test._id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                {i + 1}. {test.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Total Questions: {test.questions.length}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Created by: {test.createdBy?.name || 'Unknown'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectTest;
